import { useEffect, useRef, useCallback, useState } from "react";
import {
  resolveLatLng,
  userDotIcon,
  buildInfoWindowHtml,
  openExternalDirections,
} from "../utils/mapUtils";
import { Restaurant } from "../interfaces/Catalogue";
import { useConfig } from "../context/ConfigContext";
import { buildIconWithBg } from "../utils/iconUtils";

interface Params {
  mapsReady: boolean;
  visible: boolean;
  restaurants: Restaurant[];
  selectedId: number | undefined;
  userLocation: { lat: number; lng: number } | null;
  primaryColor: string;
  logoUrl: string;
  onSelect: (r: Restaurant) => void;
  onItemFocus: (id: number) => void;
}

export const useRestaurantMap = ({
  mapsReady,
  visible,
  restaurants,
  selectedId,
  userLocation,
  primaryColor,
  logoUrl,
  onSelect,
  onItemFocus,
}: Params) => {
  const { config } = useConfig();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const markerMapRef = useRef<Map<number, google.maps.Marker>>(new Map());
  const userMarkerRef = useRef<google.maps.Marker | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const directionsRendererRef = useRef<google.maps.DirectionsRenderer | null>(null);

  const [routeActive, setRouteActive] = useState(false);
  const [routeInfo, setRouteInfo] = useState<{ distance: string; duration: string } | null>(null);

  // Draw an in-app route from user → restaurant
  const drawRoute = useCallback(
    async (r: Restaurant) => {
      const map = mapInstanceRef.current;
      if (!map || !userLocation) return false;
      const dest = await resolveLatLng(r);
      if (!dest) return false;

      if (!directionsRendererRef.current) {
        directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
          suppressMarkers: false,
          polylineOptions: { strokeColor: primaryColor, strokeWeight: 5, strokeOpacity: 0.85 },
        });
        directionsRendererRef.current.setMap(map);
      }

      const service = new window.google.maps.DirectionsService();
      return new Promise<boolean>((resolve) => {
        service.route(
          {
            origin: userLocation,
            destination: dest,
            travelMode: window.google.maps.TravelMode.DRIVING,
          },
          (result, status) => {
            if (status === "OK" && result) {
              directionsRendererRef.current!.setDirections(result);
              const leg = result.routes[0]?.legs[0];
              setRouteInfo({
                distance: leg?.distance?.text ?? "",
                duration: leg?.duration?.text ?? "",
              });
              setRouteActive(true);
              resolve(true);
            } else {
              resolve(false);
            }
          }
        );
      });
    },
    [userLocation, primaryColor]
  );

  const clearRoute = useCallback(() => {
    directionsRendererRef.current?.setDirections({ routes: [] } as any);
    setRouteActive(false);
    setRouteInfo(null);
  }, []);

  const recenter = useCallback(() => {
    if (!mapInstanceRef.current || !userLocation) return;
    mapInstanceRef.current.panTo(userLocation);
    mapInstanceRef.current.setZoom(14);
  }, [userLocation]);

  const focusRestaurant = useCallback((id: number) => {
    const marker = markerMapRef.current.get(id);
    if (marker && mapInstanceRef.current) {
      mapInstanceRef.current.panTo(marker.getPosition()!);
      mapInstanceRef.current.setZoom(15);
      window.google.maps.event.trigger(marker, "click");
    }
  }, []);

  // User dot
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapsReady) return;
    if (!userLocation) {
      userMarkerRef.current?.setMap(null);
      userMarkerRef.current = null;
      return;
    }
    if (userMarkerRef.current) {
      userMarkerRef.current.setPosition(userLocation);
    } else {
      userMarkerRef.current = new window.google.maps.Marker({
        position: userLocation,
        map,
        title: "Ma position",
        icon: {
          url: userDotIcon(),
          scaledSize: new window.google.maps.Size(28, 28),
          anchor: new window.google.maps.Point(14, 14),
        },
        zIndex: 20,
        clickable: false,
      });
    }
  }, [userLocation, mapsReady]);

  // Build map + markers
  useEffect(() => {
    if (!mapsReady || !visible || !mapRef.current || !restaurants.length) return;

    const map = new window.google.maps.Map(mapRef.current, {
      zoom: 12,
      center: userLocation ?? { lat: 48.8566, lng: 2.3522 },
      disableDefaultUI: true,
      gestureHandling: "greedy",
      styles: [
        { featureType: "poi", stylers: [{ visibility: "off" }] },
        { featureType: "transit", stylers: [{ visibility: "off" }] },
      ],
    });
    mapInstanceRef.current = map;
    markerMapRef.current.clear();

    const iw = new window.google.maps.InfoWindow();
    infoWindowRef.current = iw;

    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    const fallback: google.maps.Symbol = {
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: 12,
      fillColor: primaryColor,
      fillOpacity: 1,
      strokeColor: "#fff",
      strokeWeight: 2,
    };

    const init = async () => {
      const iconDataUrl = logoUrl ? await buildIconWithBg(logoUrl, config?.layoutColor) : null;
      const markerIcon: google.maps.Icon | undefined = iconDataUrl
        ? { url: iconDataUrl, scaledSize: new window.google.maps.Size(36, 36), anchor: new window.google.maps.Point(18, 36) }
        : undefined;

      const positions = await Promise.all(restaurants.map(resolveLatLng));
      const bounds = new window.google.maps.LatLngBounds();
      let any = false;

      restaurants.forEach((r, i) => {
        const pos = positions[i];
        if (!pos) return;
        any = true;
        bounds.extend(pos);

        const isSelected = selectedId === r.id;
        const marker = new window.google.maps.Marker({
          position: pos,
          map,
          title: r.nom,
          icon: markerIcon ?? fallback,
          animation: window.google.maps.Animation.DROP,
          zIndex: isSelected ? 10 : 1,
        });
        markersRef.current.push(marker);
        markerMapRef.current.set(r.id, marker);

        marker.addListener("click", () => {
          onItemFocus(r.id);
          iw.setContent(buildInfoWindowHtml(r, primaryColor, selectedId === r.id));
          iw.open(map, marker);

          window.google.maps.event.addListenerOnce(iw, "domready", () => {
            document.getElementById(`msm-sel-${r.id}`)?.addEventListener("click", () => {
              onSelect(r);
              iw.close();
            });
            document.getElementById(`msm-route-${r.id}`)?.addEventListener("click", () => {
              iw.close();
              drawRoute(r);
            });
            document.getElementById(`msm-dir-${r.id}`)?.addEventListener("click", () => {
              openExternalDirections(r, userLocation);
            });
          });
        });
      });

      if (any) {
        if (userLocation) {
          map.panTo(userLocation);
          map.setZoom(13);
        } else {
          map.fitBounds(bounds);
          window.google.maps.event.addListenerOnce(map, "idle", () => {
            if ((map.getZoom() ?? 0) > 15) map.setZoom(15);
          });
        }
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapsReady, visible, restaurants, selectedId, primaryColor, logoUrl]);

  // Pan when user location arrives late
  useEffect(() => {
    if (!mapInstanceRef.current || !userLocation) return;
    mapInstanceRef.current.panTo(userLocation);
    mapInstanceRef.current.setZoom(13);
  }, [userLocation]);

  return {
    mapRef,
    drawRoute,
    clearRoute,
    recenter,
    focusRestaurant,
    routeActive,
    routeInfo,
  };
};